import React from 'react';
import { useLocation } from 'react-router-dom';
import { getPublicSeo, PUBLIC_SITE_URL, SOCIAL_LINKS } from '../config/publicSeo';

const DEFAULT_TITLE = 'Bautista Planning and Analytics';

function upsertMeta(selector, attributes) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement('meta');
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
}

export default function PublicSeoMetadata() {
  const location = useLocation();

  React.useEffect(() => {
    const seo = getPublicSeo(location.pathname);
    if (!seo) {
      // Pages that set their own title do so after this, in their own effects.
      document.title = DEFAULT_TITLE;
      return undefined;
    }

    document.title = seo.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: seo.description });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: seo.canonicalUrl });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seo.canonicalUrl);

    let structuredData = document.head.querySelector('#public-page-structured-data');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'public-page-structured-data';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }

    const graph = [
      {
        '@type': 'Organization',
        '@id': `${PUBLIC_SITE_URL}/#organization`,
        name: 'Bautista Planning and Analytics',
        url: PUBLIC_SITE_URL,
        sameAs: SOCIAL_LINKS.map((item) => item.href),
      },
      {
        '@type': 'WebSite',
        '@id': `${PUBLIC_SITE_URL}/#website`,
        name: 'Bautista Planning and Analytics',
        url: PUBLIC_SITE_URL,
        publisher: { '@id': `${PUBLIC_SITE_URL}/#organization` },
      },
      {
        '@type': 'WebPage',
        '@id': `${seo.canonicalUrl}#webpage`,
        url: seo.canonicalUrl,
        name: seo.title,
        description: seo.description,
        isPartOf: { '@id': `${PUBLIC_SITE_URL}/#website` },
      },
    ];

    if (seo.isCalculator) {
      graph.push({
        '@type': 'WebApplication',
        name: seo.title.replace(' | Bautista Planning', ''),
        url: seo.canonicalUrl,
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      });
    }

    structuredData.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
    return undefined;
  }, [location.pathname]);

  return null;
}
